import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { PUZZLE_CONFIG } from '../config/puzzleConfig';
import { usePuzzle } from '../hooks/usePuzzle';
import { formatTime } from '../utils/timeUtils';
import PuzzleBoard from './PuzzleBoard';
import PuzzleHUD from './PuzzleHUD';
import YoutubeIframe_L from './YoutubeIframe_L';

const GameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: transparent;
  height: 100%;
  font-family: 'Arial', sans-serif;
  border-radius: 20px;
  width: 100%;
  max-width: 800px;
  overflow: hidden;
  justify-content: flex-start;
  ${({ isOverlay }) => !isOverlay && 'backdrop-filter: blur(10px);'}

  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 15px;
  }
`;

const VideoWrapper = styled.div`
  position: ${({ isOverlay }) => (isOverlay ? 'fixed' : 'static')};
  width: ${({ isOverlay }) => (isOverlay ? '90%' : 'auto')};
  max-width: ${({ isOverlay }) => (isOverlay ? 'auto' : 'auto')};
  aspect-ratio: 16/9;
  z-index: ${({ isOverlay }) => (isOverlay ? 10 : 'auto')};
  transition: all 0.5s ease-in-out;
  border-radius: ${({ isOverlay }) => (isOverlay ? '15px' : '4px')};
  overflow: hidden;
  margin: ${({ isOverlay }) => (isOverlay ? '0' : '0')};
  transform: ${({ isOverlay }) => (isOverlay ? 'translate(-50%, -50%) scale(1)' : 'translateX(0) translateY(0) scale(1)')};
  left: 50%;
  top: 50%;

  @media (max-width: 768px) {
    height: ${({ isOverlay }) => (isOverlay ? '45%' : '100px')};
    width: ${({ isOverlay }) => (isOverlay ? 'auto' : '177px')};
    transform: ${({ isOverlay }) => (isOverlay ? 'translate(-50%, -70%) scale(1)' : 'translateX(0) translateY(0) scale(1)')};
      }
`;

const GameContent = styled.div`
  opacity: ${props => props.show ? '1' : '0'};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(20px)'};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
`;

const VideoSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

const GameArea = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  flex: 1;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    align-items: center;
    width: 100%;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: row;
    gap: 15px;
    width: 100%;
    height: 110px;
  }
`;

const ImageSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  opacity: ${props => props.isOverlay ? '0' : '1'};
  ${props => props.isOverlay && 'transition: opacity 0.3s ease;'};
  pointer-events: ${props => props.isOverlay ? 'none' : 'auto'};
  cursor: pointer;
  z-index: 2000;
  transform-origin: ${props => props.isMobile ? 'bottom center' : 'top center'};
`;

const OverlayMessage = styled.div`
  color: white;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  margin: 20px 0;
  transform: translateY(50px);

  @media (max-width: 768px) {
    font-size: 12px;
    transform: translateY(80px);
  }
`;

const StageMessage = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  
  .message-content {
    background: rgba(0, 0, 0, 0.95);
    color: white;
    padding: 40px;
    border-radius: 20px;
    text-align: center;
    max-width: 450px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.7);
    border: 2px solid rgba(76, 175, 80, 0.3);
    margin: 20px;
  }

  h3 {
    margin: 0 0 20px 0;
    font-size: 28px;
    color: #4CAF50;
    font-weight: bold;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  p {
    margin: 0 0 25px 0;
    font-size: 18px;
    line-height: 1.6;
    color: #f0f0f0;
  }

  button {
    background: linear-gradient(135deg, #4CAF50, #45a049);
    color: white;
    border: none;
    padding: 15px 30px;
    border-radius: 12px;
    font-size: 18px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);

    &:hover {
      background: linear-gradient(135deg, #45a049, #4CAF50);
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
    }

    &:active {
      transform: translateY(-1px);
    }
  }

  @media (max-width: 768px) {
    .message-content {
      padding: 25px;
      max-width: 320px;
      margin: 15px;
    }

    h3 {
      font-size: 22px;
      margin-bottom: 15px;
    }

    p {
      font-size: 16px;
      margin-bottom: 20px;
    }

    button {
      padding: 12px 24px;
      font-size: 16px;
    }
  }
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TopControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BottomControls = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  justify-content: center;
  align-items: center;
  height: 100%;

  @media (max-width: 768px) {
    height: 100px;
    margin-bottom: 10px;
  }
`;

const Button = styled.button`
  padding: ${props => props.small ? '6px 12px' : '12px 24px'};
  border: none;
  border-radius: 8px;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return '#4CAF50';
      case 'secondary': return '#f44336';
      case 'tertiary': return '#2196F3';
      default: return '#4CAF50';
    }
  }};
  color: white;
  font-size: ${props => props.small ? '14px' : '16px'};
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: ${props => props.small ? '5px 10px' : '10px 20px'};
    font-size: ${props => props.small ? '12px' : '14px'};
  }
`;

const SizeButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 6px;
  }
`;

const SizeButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: ${props => props.active ? '#4CAF50' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#4CAF50' : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const OriginalImage = styled.div`
  width: 150px;
  height: 150px;
  background: url(${props => props.imageUrl}) center;
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 8px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  margin-bottom: 15px;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    margin-bottom: 10px;
  }
`;

const PuzzleGame = ({ onBackToHome, onComplete, is4x4Mode = false }) => {
  const imageSectionRef = useRef(null);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [showVideo, setShowVideo] = useState(true);
  const [videoStarted, setVideoStarted] = useState(false);
  const [videoMini, setVideoMini] = useState(false); // 초기에 큰사이즈로 시작
  const [gameStarted, setGameStarted] = useState(true); // 초기에 게임 시작
  const [hasMovedPuzzle, setHasMovedPuzzle] = useState(false); // 퍼즐 움직임 여부
  const [elapsedTime, setElapsedTime] = useState(0); // 경과 시간
  const youtubeRef = useRef(null);
  const [isOverlay, setIsOverlay] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // 단계별 게임 상태 제거 - 항상 3x3으로 고정
  // const [gameStage, setGameStage] = useState(1); // 1: 3x3, 2: 4x4
  // const [showStageMessage, setShowStageMessage] = useState(false);

  const {
    puzzleSize,
    puzzle,
    moves,
    isComplete,
    movingPiece,
    handlePieceClick,
    changePuzzleSize,
    shufflePuzzle,
    resetGame
  } = usePuzzle();

  // 모바일/웹 구분
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 퍼즐 게임 리셋 시 hasMovedPuzzle도 리셋
  const handleResetGame = () => {
    resetGame();
    setHasMovedPuzzle(false);
    setElapsedTime(0);
    setIsOverlay(true);
    // setGameStage(1); // 1단계로 리셋 - 제거
    // setShowStageMessage(false); // 제거
  };

  // 4x4 모드일 때 퍼즐 크기 설정
  useEffect(() => {
    if (is4x4Mode) {
      changePuzzleSize(4);
    }
  }, [is4x4Mode, changePuzzleSize]);

  // 퍼즐 완료 시 바로 onComplete 호출
  useEffect(() => {
    if (isComplete) {
      // 퍼즐 완성 시 게임 완료 처리
      if (onComplete) {
        onComplete({
          moves: moves,
          timeSpent: formatTime(elapsedTime),
          timeSpentMs: elapsedTime, // 숫자 필드 추가
          stage: is4x4Mode ? 2 : 1, // 4x4는 stage 2, 3x3은 stage 1
          puzzleSize: puzzleSize
        });
      }
    }
  }, [isComplete, moves, onComplete, elapsedTime, puzzleSize, is4x4Mode]);

  const handleVideoStart = () => {
    setVideoStarted(true);
    // 5초 뒤 overlay → mini 모드로 전환
    setTimeout(() => setIsOverlay(false), 5000);
  };

  const handleVideoMini = () => {
    //console.log('Video mini transition in PuzzleGame');
    setVideoMini(true);
    setGameStarted(true);
  };

  const handleVideoReady = () => {
    // 비디오가 준비되면 플레이 버튼이 나타남
  };

  // 다음 단계로 진행하는 함수 제거
  // const handleNextStage = () => {
  //   setGameStage(2); // 4x4로 변경
  //   setShowStageMessage(false);
  //   setHasMovedPuzzle(false);
  //   setElapsedTime(0);
  //   changePuzzleSize(4); // 4x4 퍼즐로 변경
  //   // 완성 상태 명시적 리셋
  //   resetGame(); // 이 함수가 isComplete를 false로 설정합니다
  // };

  // 퍼즐 조각 클릭 처리 (비디오 재생 포함)
  const handlePuzzleClick = (index, e) => {
    setHasMovedPuzzle(true);
    
    // 첫 번째 퍼즐 이동 시 비디오 재생
    //if (!hasMovedPuzzle && e.nativeEvent.isTrusted) {
      // if (youtubeRef.current && youtubeRef.current.playVideo) {
      //   youtubeRef.current.playVideo();
      // }
    //}
    
    // 퍼즐 조각 클릭 처리
    handlePieceClick(index);
  };

  return (
    <GameContainer isOverlay={isOverlay}>
      {isOverlay && (
        <OverlayMessage>
          영상 재생 5초 후 퍼즐 게임이 시작됩니다.
        </OverlayMessage>
      )}
      
      {/* 스테이지 전환 메시지 제거 */}
      {/* {showStageMessage && (
         <StageMessage
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           transition={{ duration: 0.3 }}
         >
           <div className="message-content">
             <h3>🎉 퍼즐 완성!</h3>
             <p>몸좀 풀었지? 이제 진짜 시작한다!</p>
             <button onClick={handleNextStage}>
               🚀 4x4 퍼즐 시작하기
             </button>
           </div>
         </StageMessage>
       )} */}
      {gameStarted && (
        <GameContent show={gameStarted}>
            <PuzzleHUD
              isOverlay={isOverlay}
              puzzleSize={puzzleSize}
              moves={moves}
              isComplete={isComplete}
              onReset={handleResetGame}
              hasMovedPuzzle={hasMovedPuzzle}
              onTimeUpdate={setElapsedTime}
              gameStage={is4x4Mode ? 2 : 1} // 4x4는 2단계, 3x3은 1단계
            />
          
          <GameArea>
            <PuzzleBoard
              isOverlay={isOverlay}
              puzzle={puzzle}
              size={puzzleSize}
              imageUrl={PUZZLE_CONFIG.imageUrl}
              movingPiece={movingPiece}
              onPieceClick={(index, e) => {
                handlePuzzleClick(index, e);
              }}
            />

            <Controls>
                <ImageSection 
                  isOverlay={isOverlay}
                  isMobile={isMobile}
                  ref={imageSectionRef}
                  animate={{ 
                    scale: isImageHovered ? 1.4 : 1,
                    transition: { 
                      duration: 0.3, 
                      ease: "easeInOut" 
                    }
                  }}
                  onHoverStart={() => {
                    if (!isMobile) {
                      setIsImageHovered(true);
                      // 3초 후 원래 크기로 돌아가기
                      setTimeout(() => {
                        setIsImageHovered(false);
                      }, 3000);
                    }
                  }}
                  onHoverEnd={() => {
                    if (!isMobile) {
                      setIsImageHovered(false);
                    }
                  }}
                  onTap={() => {
                    if (isMobile) {
                      setIsImageHovered(true);
                      // 3초 후 원래 크기로 돌아가기
                      setTimeout(() => {
                        setIsImageHovered(false);
                      }, 3000);
                    }
                  }}
                >
                  <OriginalImage imageUrl={PUZZLE_CONFIG.imageUrl} />
                </ImageSection>
              
              <ButtonSection>
                <BottomControls>
                  {/* <Button variant="tertiary" small onClick={onBackToHome}>
                    🏠 홈으로
                  </Button> */}
                  <VideoWrapper isOverlay={isOverlay}>
                  <YoutubeIframe_L
                    ref={youtubeRef}
                    videoUrl={PUZZLE_CONFIG.videoUrl} // https://youtu.be/TniiCQ7VumI
                    onVideoStart={handleVideoStart}
                    onVideoMini={handleVideoMini}
                    onReady={handleVideoReady}
                    isMini={!isOverlay}
                  />
                  </VideoWrapper>
                </BottomControls>
              </ButtonSection>
            </Controls>
          </GameArea>
        </GameContent>
      )}
    </GameContainer>
  );
};

export default PuzzleGame;